import { useCallback, useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import type { FieldErrors } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { useTranslation } from '@/i18n';
import { useUI } from '@/hooks/ui/useUI';
import { useCatalogLoader } from '@/hooks/ui/useCatalogLoader';
import { useSidePanelLoading } from '@/hooks/ui/useSidePanelLoading';
import { holidayService } from '@/api/domains/attendance';
import { holidayTypeCatalogService } from '@/api/domains/catalog/holidayType';
import type { HolidayType } from '@/api/domains/catalog/holidayType';
import { injectValidationErrors } from '@/utils/formValidationErrors';
import { formatDateToYYYYMMDD } from '@/utils';
import { createHolidaySchema, type CreateHolidayFormData } from '@/schemas/attendance/createHolidayForm';
import { bulkCreateHolidaySchema, type BulkCreateHolidayFormData } from '@/schemas/attendance/bulkCreateHolidayForm';


const INHABIL_TYPE_NAME = 'Inhábil';

function countWeekdayCandidates(year: number, weekdays: number[]): number {
  const date = new Date(year, 0, 1);
  let count = 0;
  while (date.getFullYear() === year) {
    const jsDay = date.getDay();
    const apiDay = jsDay === 0 ? 7 : jsDay;
    if (weekdays.includes(apiDay)) count++;
    date.setDate(date.getDate() + 1);
  }
  return count;
}

interface UseCreateHolidayFormPanelParams {
  onSubmit?: () => void;
  readOnly?: boolean;
}

export interface UseCreateHolidayFormPanelResult {
  holidayTypeValue: string;
  holidayTypeOptions: { value: string; label: string }[];
  dateValue: string;
  reasonValue: string;
  nonWaivableValue: boolean;
  isInhabilesMode: boolean;
  fullYearChecked: boolean;
  isBulkMode: boolean;
  yearValue: string;
  yearOptions: { value: string; label: string }[];
  selectedWeekdays: number[];
  effectiveReadOnly: boolean;
  isSubmitting: boolean;
  isReady: boolean;
  errors: FieldErrors<CreateHolidayFormData>;
  bulkErrors: FieldErrors<BulkCreateHolidayFormData>;
  isSubmitted: boolean;
  bulkIsSubmitted: boolean;
  handleHolidayTypeChange: (value: string | string[]) => void;
  handleDateChange: (value: string) => void;
  handleReasonChange: (value: string) => void;
  handleNonWaivableChange: (checked: boolean) => void;
  handleFullYearChange: (checked: boolean) => void;
  handleYearChange: (value: string | string[]) => void;
  handleWeekdayToggle: (weekday: number, checked: boolean) => void;
  handleSubmit: (event?: FormEvent<HTMLFormElement>) => void;
}

export function useCreateHolidayFormPanel({
  onSubmit,
  readOnly,
}: UseCreateHolidayFormPanelParams): UseCreateHolidayFormPanelResult {
  const { t: translate } = useTranslation();
  const { showSuccess, showApiError, showError, showInfo, showWarning, closeSidePanel } = useUI();
  const { setLoading } = useSidePanelLoading();

  const effectiveReadOnly = !!readOnly;
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const today = useMemo(() => formatDateToYYYYMMDD(new Date()), []);

  // ── Local field state — normal form ────────────────────────────────────
  const [holidayTypeValue, setHolidayTypeValue] = useState<string>('');
  const [dateValue, setDateValue] = useState<string>(today);
  const [reasonValue, setReasonValue] = useState<string>('');
  const [nonWaivableValue, setNonWaivableValue] = useState<boolean>(false);

  // ── Local field state — bulk form ──────────────────────────────────────
  const [fullYearChecked, setFullYearChecked] = useState<boolean>(false);
  const [yearValue, setYearValue] = useState<string>('');
  const [selectedWeekdays, setSelectedWeekdays] = useState<number[]>([]);

  // ── Catalog loading ────────────────────────────────────────────────────
  const holidayTypeCatalog = useCatalogLoader<HolidayType>(
    (signal) => holidayTypeCatalogService.getHolidayTypes(signal),
    (item) => ({ value: String(item.id), label: item.name }),
    [],
    false,
    () => showError(translate('attendance:holiday.messages.loadError')),
  );

  useEffect(() => {
    setLoading(!holidayTypeCatalog.isLoaded);
  }, [holidayTypeCatalog.isLoaded, setLoading]);

  // ── Mode detection ─────────────────────────────────────────────────────
  const isInhabilesMode = useMemo(() => {
    if (!holidayTypeValue) return false;
    const selectedType = holidayTypeCatalog.data.find(
      (type) => String(type.id) === holidayTypeValue,
    );
    return selectedType?.name === INHABIL_TYPE_NAME;
  }, [holidayTypeValue, holidayTypeCatalog.data]);

  const isBulkMode = isInhabilesMode && fullYearChecked;

  // ── Year options ───────────────────────────────────────────────────────
  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 7 }, (_, i) => {
      const year = currentYear - 1 + i;
      return { value: String(year), label: String(year) };
    });
  }, []);

  // ── Reset bulk fields when leaving inhábil mode ────────────────────────
  useEffect(() => {
    if (!isInhabilesMode) {
      setFullYearChecked(false);
      setYearValue('');
      setSelectedWeekdays([]);
    }
  }, [isInhabilesMode]);

  // ── Reset bulk fields when unchecking fullYear ─────────────────────────
  useEffect(() => {
    if (!fullYearChecked) {
      setYearValue('');
      setSelectedWeekdays([]);
    }
  }, [fullYearChecked]);

  // ── Normal form (React Hook Form) ──────────────────────────────────────
  const {
    handleSubmit: rhfHandleSubmit,
    setValue,
    setError,
    register,
    unregister,
    formState: { errors, isSubmitted },
  } = useForm<CreateHolidayFormData>({
    resolver: zodResolver(createHolidaySchema),
    mode: 'onSubmit',
    defaultValues: {
      holidayTypeId: null,
      date: today,
      reason: '',
      nonWaivable: false,
    },
  });

  useEffect(() => {
    register('holidayTypeId');
    register('date');
    register('reason');
    register('nonWaivable');
    return () => {
      unregister('holidayTypeId');
      unregister('date');
      unregister('reason');
      unregister('nonWaivable');
    };
  }, [register, unregister]);

  useEffect(() => {
    setValue('holidayTypeId', holidayTypeValue ? Number(holidayTypeValue) : null);
  }, [holidayTypeValue, setValue]);
  useEffect(() => { setValue('date', dateValue); }, [dateValue, setValue]);
  useEffect(() => { setValue('reason', reasonValue); }, [reasonValue, setValue]);
  useEffect(() => { setValue('nonWaivable', nonWaivableValue); }, [nonWaivableValue, setValue]);

  // ── Bulk form (React Hook Form) ────────────────────────────────────────
  const {
    handleSubmit: bulkRhfHandleSubmit,
    setValue: bulkSetValue,
    setError: bulkSetError,
    register: bulkRegister,
    unregister: bulkUnregister,
    formState: { errors: bulkErrors, isSubmitted: bulkIsSubmitted },
  } = useForm<BulkCreateHolidayFormData>({
    resolver: zodResolver(bulkCreateHolidaySchema),
    mode: 'onSubmit',
    defaultValues: {
      holidayTypeId: null,
      year: null,
      weekdays: [],
    },
  });

  useEffect(() => {
    bulkRegister('holidayTypeId');
    bulkRegister('year');
    bulkRegister('weekdays');
    return () => {
      bulkUnregister('holidayTypeId');
      bulkUnregister('year');
      bulkUnregister('weekdays');
    };
  }, [bulkRegister, bulkUnregister]);

  useEffect(() => {
    bulkSetValue('holidayTypeId', holidayTypeValue ? Number(holidayTypeValue) : null);
  }, [holidayTypeValue, bulkSetValue]);
  useEffect(() => {
    bulkSetValue('year', yearValue ? Number(yearValue) : null);
  }, [yearValue, bulkSetValue]);
  useEffect(() => {
    bulkSetValue('weekdays', selectedWeekdays);
  }, [selectedWeekdays, bulkSetValue]);

  // ── isReady ────────────────────────────────────────────────────────────
  const isReady = useMemo(() => {
    if (!holidayTypeCatalog.isLoaded || !holidayTypeValue) return false;
    if (isBulkMode) return yearValue.length > 0 && selectedWeekdays.length > 0;
    return dateValue.length > 0 && reasonValue.trim().length > 0;
  }, [
    holidayTypeCatalog.isLoaded,
    holidayTypeValue,
    isBulkMode,
    yearValue,
    selectedWeekdays,
    dateValue,
    reasonValue,
  ]);

  // ── Handlers ───────────────────────────────────────────────────────────
  const handleHolidayTypeChange = useCallback((value: string | string[]) => {
    setHolidayTypeValue(Array.isArray(value) ? (value[0] ?? '') : value);
  }, []);

  const handleDateChange = useCallback((value: string) => {
    setDateValue(value);
  }, []);

  const handleReasonChange = useCallback((value: string) => {
    setReasonValue(value);
  }, []);

  const handleNonWaivableChange = useCallback((checked: boolean) => {
    setNonWaivableValue(checked);
  }, []);

  const handleFullYearChange = useCallback((checked: boolean) => {
    setFullYearChecked(checked);
  }, []);

  const handleYearChange = useCallback((value: string | string[]) => {
    setYearValue(Array.isArray(value) ? (value[0] ?? '') : value);
  }, []);

  const handleWeekdayToggle = useCallback((weekday: number, checked: boolean) => {
    setSelectedWeekdays((prev) =>
      checked ? [...prev, weekday] : prev.filter((d) => d !== weekday),
    );
  }, []);

  // ── Submit — normal mode ───────────────────────────────────────────────
  const onValidNormalSubmit = useCallback(
    async (values: CreateHolidayFormData) => {
      if (effectiveReadOnly) return;
      setIsSubmitting(true);

      try {
        const response = await holidayService.create({
          holidayTypeId: values.holidayTypeId as number,
          date: values.date,
          reason: values.reason,
          nonWaivable: values.nonWaivable,
        });

        if (!response.success) {
          if (response.error?.validationErrors && response.error.validationErrors.length > 0) {
            injectValidationErrors(response.error.validationErrors, { setError });
            return;
          }
          showApiError(response, translate('attendance:holiday.messages.createError'));
          return;
        }

        showSuccess(translate('attendance:holiday.messages.createSuccess'));
        closeSidePanel();
        onSubmit?.();
      } finally {
        setIsSubmitting(false);
      }
    },
    [effectiveReadOnly, setError, showApiError, showSuccess, closeSidePanel, onSubmit, translate],
  );

  // ── Submit — bulk mode ─────────────────────────────────────────────────
  const onValidBulkSubmit = useCallback(
    async (values: BulkCreateHolidayFormData) => {
      if (effectiveReadOnly) return;
      setIsSubmitting(true);

      try {
        const response = await holidayService.bulkCreate({
          holidayTypeId: values.holidayTypeId as number,
          year: values.year as number,
          weekdays: values.weekdays,
        });

        if (!response.success) {
          if (response.error?.validationErrors && response.error.validationErrors.length > 0) {
            injectValidationErrors(response.error.validationErrors, { setError: bulkSetError });
            return;
          }
          showApiError(response, translate('attendance:holiday.messages.bulkCreateError'));
          return;
        }

        if (!response.data?.hasInsertions) {
          showWarning(translate('attendance:holiday.messages.bulkCreateNoRecords'));
          return;
        }

        const totalCandidates = countWeekdayCandidates(values.year as number, values.weekdays);
        const omitted = totalCandidates - (response.data.totalCreated ?? 0);

        if (omitted > 0) {
          showInfo(
            translate('attendance:holiday.messages.bulkCreatePartial', { count: omitted }),
          );
        } else {
          showSuccess(translate('attendance:holiday.messages.bulkCreateSuccess'));
        }

        closeSidePanel();
        onSubmit?.();
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      effectiveReadOnly,
      bulkSetError,
      showApiError,
      showInfo,
      showWarning,
      showSuccess,
      closeSidePanel,
      onSubmit,
      translate,
    ],
  );

  // ── Unified handleSubmit ───────────────────────────────────────────────
  const handleSubmit = useCallback(
    (event?: FormEvent<HTMLFormElement>) => {
      event?.preventDefault();
      if (isBulkMode) {
        void bulkRhfHandleSubmit(onValidBulkSubmit)(
          event as unknown as React.BaseSyntheticEvent,
        );
      } else {
        void rhfHandleSubmit(onValidNormalSubmit)(
          event as unknown as React.BaseSyntheticEvent,
        );
      }
    },
    [isBulkMode, bulkRhfHandleSubmit, onValidBulkSubmit, rhfHandleSubmit, onValidNormalSubmit],
  );

  return {
    holidayTypeValue,
    holidayTypeOptions: holidayTypeCatalog.options,
    dateValue,
    reasonValue,
    nonWaivableValue,
    isInhabilesMode,
    fullYearChecked,
    isBulkMode,
    yearValue,
    yearOptions,
    selectedWeekdays,
    effectiveReadOnly,
    isSubmitting,
    isReady,
    errors,
    bulkErrors,
    isSubmitted,
    bulkIsSubmitted,
    handleHolidayTypeChange,
    handleDateChange,
    handleReasonChange,
    handleNonWaivableChange,
    handleFullYearChange,
    handleYearChange,
    handleWeekdayToggle,
    handleSubmit,
  };
}

export default useCreateHolidayFormPanel;
